import { useState } from 'react';
import cn from 'classnames';

/* i18n */
import { useIntl } from 'react-intl';

/* Styles */
import styles from 'styles/recommendations-card.module.scss';
import layoutStyles from 'styles/layout.module.scss';

/* Assets */
import thumbsUp from 'assets/images/ThumbsUpBlue.svg';
import thumbsDown from 'assets/images/ThumbsDownBlue.svg';
import thumbsUpFilled from 'assets/images/ThumbsUp_Small_Filled.svg';
import thumbsDownFilled from 'assets/images/ThumbsDown_Small_Filled.svg';
import arrowRight from 'assets/images/Arrow_Right.svg';
import info from 'assets/images/Info_Cards.svg';
import Close from 'assets/images/Close';

/* Material UI and other UI Dependencies */
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';

/* Redux */
import { useDispatch } from 'react-redux';
import { setChatFeedback } from 'app/slices/appSlice';

/* UI Components */
import Tag from "components/Tag";
import CareerVideo from './CareerVideo';

const RecommendationsCard = (props) => {
  const {
    item,
    rating,
    type,
    link,
    rangeLabel,
    titleHoverable = false,
    onButtonClick = ()=>{},
    onTitleClick = ()=>{},
    onLikeClick = ()=>{},
    onDislikeClick = ()=>{},
    onTagClick = ()=>{},
  } = props;

  const [ expandCard, setExpandCard ] = useState(false);
  const [ haveExpandedCard, setHaveExpandedCard ] = useState(false);
  const intl = useIntl();
  const dispatch = useDispatch();
  const flagsDict = {
    career: 'careers',
    job: 'jobs'
  };

  const handleTitleClick = () => {
    onTitleClick();
    if (titleHoverable) {
      setExpandCard(true);
      setHaveExpandedCard(true);
    }
  }

  const onLikeClickLocal = () => {
    if (rating !== 'liked') {
      dispatch(setChatFeedback({ action: 'LIKE_REC', item: props }));
    }
    onLikeClick();
  }

  const onDislikeClickLocal = () => {
    if (rating !== 'disliked') {
      dispatch(setChatFeedback({ action: 'DISLIKE_REC', item: props }));
    }
    onDislikeClick();
  }

  let showNewJobDot = false;
  if (type === 'job') {
    var yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const datePosted = Date.UTC(
      parseInt(item.date_posted.substr(0, 4)),
      parseInt(item.date_posted.substr(4, 2))-1,
      parseInt(item.date_posted.substr(6, 2))
    );
    if (datePosted === yesterday) {
      showNewJobDot = true;
    }
  }

  return(
    <>
    <div className={cn({
      [styles.card]: true,
      [styles.careerType]: type === 'career',
      [styles.jobType]: type === 'job',
      [styles.trainingType]: type === 'training'
    })}>
      {item.flag && (
        <>
          <div className={cn({
            [styles.flag]: true,
            [styles.careerFlag]: type === 'career',
            [styles.jobFlag]: type === 'job',
            [styles.trainingFlag]: type === 'training'
          })}>
            {intl.formatMessage(
              {id: `${flagsDict[type]}.flags.${item.flag}`, description:'string'}
            )}
          </div>
          <span className={cn({
            [styles.flagCorner]: true,
            [styles.careerFlag__Corner]: type === 'career',
            [styles.jobFlag__Corner]: type === 'job',
            [styles.trainingFlag__Corner]: type === 'training'
          })} />
        </>
      )}
      <Typography
        className={cn({
          [styles.title]: true,
          [styles.titleHoverable]: titleHoverable,
          [styles.titleNoMargin]: ['job', 'training'].indexOf(type) > -1
        })}
        variant="h4"
        component="h3"
        gutterBottom
        onClick={handleTitleClick}
      >
        {showNewJobDot ? <div className={styles.dotNewJob} /> : null}
        {item.title}
        <img alt="Info" src={info} className={styles.infoIcon} />
      </Typography>
      {item.org && (
        <div className={styles.company}>
          <span className={cn(
            layoutStyles.descriptorBold,
          )}>
            {item.org}
          </span>
          <span className={cn(
            layoutStyles.descriptor,
          )}>
            {item.location}
          </span>
        </div>
      )}
      <div className={styles.likesMatchTags}>
        <div>
          <span className={cn({[styles.thumbsContainer]: true, [styles.thumbsHighlighted]: rating === "liked"})} onClick={onLikeClickLocal}>
            <img alt="Thumbs Up" src={thumbsUp} className={cn(styles.thumbsButton, styles.outlinedThumbs)} />
            <img alt="Thumbs Up" src={thumbsUpFilled} className={cn(styles.thumbsButton, styles.filledThumbs)} />
          </span>
          <span className={cn({[styles.thumbsContainer]: true, [styles.thumbsHighlighted]: rating === "disliked"})} onClick={onDislikeClickLocal}>
            <img alt="Thumbs Down" src={thumbsDown} className={cn(styles.thumbsButton, styles.outlinedThumbs)} />
            <img alt="Thumbs Down" src={thumbsDownFilled} className={cn(styles.thumbsButton, styles.filledThumbs)} />
          </span>
          {
            item.match_rating && (
              <>
                <span className={cn(
                  layoutStyles.descriptor,
                  layoutStyles.bold,
                  styles.matchText,
                )}>
                  {item.match_rating}%
                </span>
                <span className={cn(
                  layoutStyles.descriptor,
                  styles.matchText,
                )}>{intl.formatMessage(
                  {id:'careers.match', description:'string'}
                )}</span>
              </>
            )
          }
        </div>
        <div className={cn({
          [styles.tags]: true,
          [styles.jobsTags]: type === 'job'
        })}>
          {item.tags && item.tags.map(t => {
            let text = `jobs.filters.${t}`;
            if (type === 'career') {
              text = `careers.tags.${t}`;
            }
            return (
              <Tag
                clickable={type === 'career' ? false : true}
                key={t}
                text={intl.formatMessage(
                {id: text, description:'string'}
              )} type={type} onClick={onTagClick} />
            );
          })}
        </div>
      </div>
      <div className={styles.button}>        
        {type === 'career' ? (
          <Button
            color="secondary"
            size="large"
            type="submit"
            variant="contained"
            onClick={onButtonClick}
          >
            <span>{item.available_jobs}</span>
            <span>&nbsp;{intl.formatMessage({id: 'careers.jobs', description: 'string'})}&nbsp;<img alt="Arrow right" src={arrowRight} className={styles.arrowRight} /></span>
          </Button>
        ) : (
          <Button
            color="secondary"
            size="large"
            type="submit"
            variant="contained"
            onClick={onButtonClick}
            href={link}
            rel='noopener nofollow'
            target='_blank'
            aria-label={intl.formatMessage({id: 'aria.opensInNewWindow', description: 'string'})}
          >
            <span>&nbsp;{intl.formatMessage({id: 'jobs.apply', description: 'string'})}&nbsp;<img alt="Arrow right" src={arrowRight} className={styles.arrowRight} /></span>
          </Button>
        )}
      </div>
      {item.salary_low && item.salary_high && (
        <div className={styles.range}>        
          <span className={cn(
              layoutStyles.descriptor,
              layoutStyles.bold
            )}>{`$${Math.floor(item.salary_low/1000)}K - $${Math.floor(item.salary_high/1000)}K / ${intl.formatMessage({id: 'careers.year'})}`}</span>
          <span className={cn(
              layoutStyles.descriptor,
              styles.rangeLabel,
            )}>{rangeLabel}</span>
        </div>
      )}
    </div>
    {
      titleHoverable ? (
        <div className={cn({
          [styles.drawer]: true,
          [styles.careerDrawer]: type === 'career',
          [styles.jobDrawer]: type === 'job',
          [styles.trainingDrawer]: type === 'training',
          [styles.drawer__visible]: expandCard,
          [styles.drawer__hidden]: !expandCard,
        })}>
          <button title={intl.formatMessage({id: 'careers.closeCareersDetails'})} className={styles.drawerCloseButton} onClick={() => setExpandCard(false)}>
            <Close className={styles.drawerCloseIcon} />
          </button>
          {item.similar_skills ? (
            <div>
              <Typography variant="h5" component="span" gutterBottom>
                {intl.formatMessage(
                  {id:'recommendations.card.similarSkills', description:'string'}
                )}
              </Typography>{': '}
              <Typography variant="body2" component="span" gutterBottom>
                {item.similar_skills}
              </Typography>
            </div>
          ) : null}
          <div className={styles.videoContainer}>
            {haveExpandedCard && <CareerVideo title={item.title} soc_code={item.soc_code} url={item.video} />}
          </div>
          <Typography variant="body2" component="p" gutterBottom>
            {item.description}
          </Typography>
          <div className={styles.drawerBottomSection}>
            <div>
              <Typography variant="h5" component="span" gutterBottom>
                {intl.formatMessage(
                  {id:'recommendations.card.brightOutlook', description:'string'}
                )}
              </Typography>{': '}
              <Typography variant="body2" component="div" gutterBottom>
                {item.bright_outlook}
              </Typography>
            </div>
            <div>
              <Typography variant="h5" component="span" gutterBottom>
                {intl.formatMessage(
                  {id:'recommendations.card.typicalTraining', description:'string'}
                )}
              </Typography>{': '}
              <Typography variant="body2" component="div" gutterBottom>
                {item.typical_training}
              </Typography>
            </div>
          </div>
        </div>
      ) : null
    }
    
    <div className={styles.margin} />
    </>
  )
}

export default RecommendationsCard;